// packages/web/src/hooks/useAuth.ts
// Authentication state management hook

import { useState, useEffect } from 'react';

import { api } from '../lib/api/client';
import { hasAuthTokens } from '../lib/catalog/preferences';

export interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
  [key: string]: any;
}

/**
 * Hook for authentication state management
 */
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        setLoading(true);

        // Check if we have auth tokens
        const hasTokens = hasAuthTokens();

        if (!hasTokens) {
          if (mounted) {
            setIsAuthenticated(false);
            setUser(null);
          }
          return;
        }

        // Try to get user info from localStorage or API
        if (typeof window !== 'undefined') {
          try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              const userData = JSON.parse(storedUser);
              if (mounted) {
                setUser(userData);
                setIsAuthenticated(true);
              }
              return;
            }
          } catch (error) {
            console.warn('[USE-AUTH] Error parsing stored user:', error);
          }
        }

        // Note: In FAANG refactor, /api/auth/me was removed.
        // User data should come from Redux store or localStorage set during login.
        console.log(
          '[USE-AUTH] No stored user data found and /api/auth/me removed in FAANG refactor',
        );
        if (mounted) {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('[USE-AUTH] Auth check failed:', error);
        if (mounted) {
          setIsAuthenticated(false);
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { setToken } = await import('../lib/api');
      const response = await api.login({ email, password });

      // Extract and set token
      const token = response.token || response.accessToken || response.jwt;
      if (token) {
        setToken(token);
      }

      // Refresh auth state
      const hasTokens = hasAuthTokens();
      setIsAuthenticated(hasTokens);

      if (hasTokens) {
        // Note: In FAANG refactor, user data is stored in Redux/localStorage during login
        // No need to fetch from API as /api/auth/me was removed
        console.log(
          '[USE-AUTH] Login completed, user data should be available in Redux/localStorage',
        );
      }

      return response;
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    }
  };

  const logout = () => {
    // Clear local storage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenantId');
    }

    // Clear cookies
    if (typeof document !== 'undefined') {
      document.cookie = 'token=; Path=/; Max-Age=0';
      document.cookie = 'faang_token=; Path=/; Max-Age=0';
    }

    setUser(null);
    setIsAuthenticated(false);
  };

  const refresh = async () => {
    try {
      setLoading(true);

      const hasTokens = hasAuthTokens();
      setIsAuthenticated(hasTokens);

      if (hasTokens) {
        // Try to get user data from localStorage (set during login)
        if (typeof window !== 'undefined') {
          try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              const userData = JSON.parse(storedUser);
              setUser(userData);
            } else {
              console.warn('[USE-AUTH] No user data in localStorage during refresh');
              setIsAuthenticated(false);
              setUser(null);
            }
          } catch (error) {
            console.warn('[USE-AUTH] Failed to parse user data from localStorage:', error);
            setIsAuthenticated(false);
            setUser(null);
          }
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('[USE-AUTH] Refresh failed:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    refresh,
    userId: user?.id,
    userEmail: user?.email,
    userName: user?.name,
    userRole: user?.role,
  };
}
